import React, { useState, useEffect } from "react";

/* Libraries */
import { Alert } from "antd";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroller";
import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";

/* Hooks */
import useBannerBackground from "hooks/useBannerBackground";
import useInfiniteQuery from "hooks/useInfiniteQuery";

/* Components */
import {
  PageContent,
  HeadingWrapper,
  HeadingGroup,
  HeadingTitle,
  SectionHeader,
  HeaderCol,
} from "common/layout";
import { SectionTitle } from "common/text";
import { CenteredLoading } from "common/loading";
import ClassList from "common/class_card/ClassList";
import {
  CityFilter,
  CityOption,
  FilterLabel,
} from "pages/book_a_class/ClassFilterStyle";
import { Row } from "pages/account/my_certificates/styles";

/* GraphQL */
import GET_BOOKABLE_CLASSES from "graphql/queries/GetBookableClasses";
import GET_CHAPTER_DETAILS from "graphql/queries/GetChapterDetails";

import { CityState, City } from "types";

const DEFAULT_PAGE_SIZE = 4;

/** Page to view all available classes of a chapter */
const ChapterDetailsPage: React.FC = () => {
  const params = useParams();
  const [filter, setFilter] = useState({
    cities: [] as CityState[],
  });
  const { backgroundImage } = useBannerBackground();
  const { t } = useTranslation("chapter_details");

  const searchParams = {
    chapterId: params.chapterId,
    cities: filter.cities
      .filter((city: CityState) => city.chosen)
      .map((city: CityState) => city.name),
    first: DEFAULT_PAGE_SIZE,
  };

  /** DATA MANAGEMENT FOR CLASS DETAILS */
  const { loading, data, error } = useQuery(GET_CHAPTER_DETAILS, {
    variables: {
      id: params.chapterId,
    },
  });

  useEffect(() => {
    if (data) {
      const cities = data.chapterDetails.cities.map((city: City) => ({
        id: city.id,
        name: city.name,
        chosen: true,
      }));
      setFilter({
        cities,
      });
    }
  }, [data]);

  const {
    nodes: bookableClasses,
    error: bookableClassesError,
    pageInfo,
    loadMore,
  } = useInfiniteQuery("bookableClasses", GET_BOOKABLE_CLASSES, {
    variables: searchParams,
    skip: params.chapterId === "0",
  });

  // Add error screen if there's an error
  if (error) {
    console.log(error);
    return (
      <Alert
        type="error"
        style={{ marginTop: "64px" }}
        message="Error. Please check connection or log in again."
        description={error.graphQLErrors
          .map(({ message }) => message)
          .join(". ")}
      />
    );
  }

  if (bookableClassesError) {
    return (
      <Alert
        type="error"
        message="Error loading classes. Please check your internet or let us know if this keeps happening."
      />
    );
  }

  // Add loading skeleton when data is being fetched
  if (loading) return <div style={{ marginTop: "64px" }}>Loading data...</div>;

  const isOptionChosen = (name: string, arr: CityState[]) => {
    const findResult = arr.find((option: CityState) => {
      return option.name === name;
    });
    return findResult?.chosen as boolean;
  };

  const onChangeCities = (name: string) => {
    const newCities = [...filter.cities];
    const chosenIndex = newCities.findIndex((city: CityState) => {
      return city.name === name;
    });

    newCities[chosenIndex].chosen = !newCities[chosenIndex].chosen;
    setFilter({
      ...filter,
      cities: newCities,
    });
  };

  const {
    chapterDetails: {
      chapter: {
        level: { title: levelName },
        name: chapterName,
      },
    },
  } = data;

  return (
    <PageContent>
      <HeadingWrapper
        style={{ justifyContent: "space-between", ...backgroundImage }}
      >
        <HeadingGroup>
          <HeadingTitle className="topic-heading">{`${levelName}, ${chapterName}`}</HeadingTitle>
        </HeadingGroup>
      </HeadingWrapper>

      <div className="past-classes section">
        <SectionHeader>
          <HeaderCol xs={24} sm={24} md={17}>
            <SectionTitle>
              {t("Book a {{ chapter }} class", {
                chapter: `${levelName} - ${chapterName}`,
              }).toUpperCase()}
            </SectionTitle>
          </HeaderCol>
          <HeaderCol xs={24} sm={24} md={7}>
            <CityFilter>
              <FilterLabel>{t("CITY VERSIONS")}:</FilterLabel>
              {filter.cities.map((city: City) => (
                <CityOption
                  key={city.id}
                  data-chosen={isOptionChosen(city.name, filter.cities)}
                  onClick={() => {
                    onChangeCities(city.name);
                  }}
                >
                  {city.name}
                </CityOption>
              ))}
            </CityFilter>
          </HeaderCol>
        </SectionHeader>
        <InfiniteScroll
          pageStart={0}
          loadMore={loadMore}
          hasMore={pageInfo?.hasNextPage}
          loader={<CenteredLoading key={pageInfo?.startCursor} />}
        >
          <Row className="section-cards" gutter={24}>
            <ClassList lessons={bookableClasses || []} allowsBooking />
          </Row>
        </InfiniteScroll>
      </div>
    </PageContent>
  );
};

export default ChapterDetailsPage;
